import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

export default function BlogArticle() {
	const { slug } = useParams();
	const navigate = useNavigate();
	const [post, setPost] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const WORDPRESS_API_URL =
		"https://blogs.steersmartdriving.com.au/wp-json/wp/v2";

	useEffect(() => {
		if (slug) {
			fetchPost();
		}
	}, [slug]);

	const fetchPost = async () => {
		try {
			setLoading(true);
			setError(null);

			const response = await fetch(
				`${WORDPRESS_API_URL}/posts?slug=${slug}&_embed`,
			);

			if (!response.ok) {
				throw new Error("Post not found");
			}

			const data = await response.json();

			if (data.length === 0) {
				throw new Error("Post not found");
			}

			setPost(data[0]);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const getTags = (post) => {
		return post._embedded?.["wp:term"]?.[1] || [];
	};

	// Enhanced content processing to handle WordPress blocks better
	const processWordPressContent = (html) => {
		// First, clean up any malformed list structures where li elements are wrapped in p tags
		let cleanedHtml = html
			// Remove p tags that wrap li elements
			.replace(/<p[^>]*>\s*(<li[^>]*>.*?<\/li>)\s*<\/p>/gi, "$1")
			// Remove p tags that wrap ul/ol elements
			.replace(/<p[^>]*>\s*(<ul[^>]*>.*?<\/ul>)\s*<\/p>/gi, "$1")
			.replace(/<p[^>]*>\s*(<ol[^>]*>.*?<\/ol>)\s*<\/p>/gi, "$1")
			// Note: Orphaned li cleanup moved after TEMP_LI processing to prevent conflicts
			// Note: Removed div li wrapping rule to prevent conflicts with TEMP_LI grouping
			// Note: Standalone li cleanup moved after TEMP_LI processing to avoid conflicts
			// Remove strong tags from inside headings to allow custom font weighting
			.replace(
				/<(h[1-6])([^>]*)>\s*<(?:strong|b)(?:\s+[^>]*)?>\s*(.*?)\s*<\/(?:strong|b)>\s*<\/\1>/gi,
				"<$1$2>$3</$1>",
			)
			// Convert paragraph tags with bullet-like content to list items
			.replace(/<p[^>]*>\s*[•·▪▫◦‣⁃]\s*(.*?)<\/p>/gi, "<TEMP_LI>$1</TEMP_LI>")
			// Convert numbered lists (1., 2., etc.)
			.replace(/<p[^>]*>\s*\d+\.\s*(.*?)<\/p>/gi, "<TEMP_LI>$1</TEMP_LI>")
			// Convert lettered lists (a., b., etc.)
			.replace(/<p[^>]*>\s*[a-zA-Z]\.\s*(.*?)<\/p>/gi, "<TEMP_LI>$1</TEMP_LI>")
			// Convert Roman numeral lists (i., ii., etc.)
			.replace(
				/<p[^>]*>\s*[ivxlcdm]+\.\s*(.*?)<\/p>/gi,
				"<TEMP_LI>$1</TEMP_LI>",
			)
			// Convert dash/hyphen lists
			.replace(/<p[^>]*>\s*[-–—]\s*(.*?)<\/p>/gi, "<TEMP_LI>$1</TEMP_LI>")
			// Group all consecutive TEMP_LI elements into a single ul
			.replace(/((?:<TEMP_LI>.*?<\/TEMP_LI>\s*)+)/gi, (match) => {
				const liItems = match
					.replace(
						/<TEMP_LI>/g,
						'<li class="mb-1 text-gray-700 leading-relaxed">',
					)
					.replace(/<\/TEMP_LI>/g, "</li>");
				return `<ul class="wp-block-list mb-6 mt-6 list-disc pl-6">${liItems}</ul>`;
			});

		// Add WordPress-specific classes and styling
		let processedHtml = cleanedHtml
			// Handle WordPress image blocks with alignment
			.replace(
				/<figure class="wp-block-image([^"]*)"([^>]*)>/g,
				'<figure class="wp-block-image$1 mb-8 mt-8"$2>',
			)
			.replace(
				/<img([^>]*)class="([^"]*)"([^>]*)>/g,
				'<img$1class="$2 rounded-lg shadow-md max-w-full h-auto"$3>',
			)

			// Handle WordPress gallery blocks
			.replace(
				/<figure class="wp-block-gallery([^"]*)"([^>]*)>/g,
				'<figure class="wp-block-gallery$1 mb-8 mt-8"$2>',
			)

			// Handle WordPress quote blocks
			.replace(
				/<blockquote class="wp-block-quote([^"]*)"([^>]*)>/g,
				'<blockquote class="wp-block-quote$1 border-l-4 border-blue-500 bg-blue-50 p-6 rounded-r-lg mb-8 mt-8 italic"$2>',
			)

			// Handle WordPress list blocks
			.replace(
				/<ul class="wp-block-list([^"]*)"([^>]*)>/g,
				'<ul class="wp-block-list$1 mb-6 mt-6 list-disc px-12"$2>',
			)
			.replace(
				/<ol class="wp-block-list([^"]*)"([^>]*)>/g,
				'<ol class="wp-block-list$1 mb-6 mt-6 list-decimal pl-6"$2>',
			)
			// Handle list items
			.replace(
				/<li([^>]*)>/g,
				'<li$1 class="mb-1 text-gray-700 leading-relaxed">',
			)
			// Handle generic lists without WordPress classes (fallback)
			.replace(
				/<ul(?![^>]*class=)([^>]*)>/g,
				'<ul$1 class="mb-6 mt-6 list-disc pl-6">',
			)
			.replace(
				/<ol(?![^>]*class=)([^>]*)>/g,
				'<ol$1 class="mb-6 mt-6 list-decimal pl-6">',
			)

			// Handle WordPress heading blocks
			.replace(
				/<h1 class="wp-block-heading([^"]*)"([^>]*)>/g,
				'<h1 class="wp-block-heading$1 text-2xl md:text-4xl font-bold text-gray-900 mb-6 mt-8 font-generalSans"$2>',
			)
			.replace(
				/<h2 class="wp-block-heading([^"]*)"([^>]*)>/g,
				'<h2 class="wp-block-heading$1 text-xl md:text-3xl font-semibold text-gray-900 mb-5 mt-8 font-generalSans"$2>',
			)
			.replace(
				/<h3 class="wp-block-heading([^"]*)"([^>]*)>/g,
				'<h3 class="wp-block-heading$1 text-lg md:text-2xl font-medium text-gray-900 mb-4 mt-6 font-generalSans"$2>',
			)
			.replace(
				/<h4 class="wp-block-heading([^"]*)"([^>]*)>/g,
				'<h4 class="wp-block-heading$1 text-base md:text-xl text-gray-900 mb-3 mt-5 font-generalSans"$2>',
			)
			.replace(
				/<h5 class="wp-block-heading([^"]*)"([^>]*)>/g,
				'<h5 class="wp-block-heading$1 text-sm md:text-lg text-gray-900 mb-3 mt-4 font-generalSans"$2>',
			)
			.replace(
				/<h6 class="wp-block-heading([^"]*)"([^>]*)>/g,
				'<h6 class="wp-block-heading$1 text-xs md:text-base text-gray-900 mb-2 mt-4 font-generalSans"$2>',
			)

			// Handle WordPress paragraph blocks
			.replace(
				/<p class="wp-block-paragraph([^"]*)"([^>]*)>/g,
				'<p class="wp-block-paragraph$1 text-gray-700 leading-relaxed mb-6"$2>',
			)

			// Handle alignment classes - CRITICAL for WordPress layout matching
			.replace(
				/class="([^"]*)\s*aligncenter([^"]*)"/g,
				'class="$1 block mx-auto text-center$2"',
			)
			.replace(
				/class="([^"]*)\s*alignleft([^"]*)"/g,
				'class="$1 float-left mr-6 mb-4 clear-left$2"',
			)
			.replace(
				/class="([^"]*)\s*alignright([^"]*)"/g,
				'class="$1 float-right ml-6 mb-4 clear-right max-w-sm$2"',
			)

			// Handle size classes for images
			.replace(/class="([^"]*)\s*size-medium([^"]*)"/g, 'class="$1 max-w-xs$2"')
			.replace(/class="([^"]*)\s*size-large([^"]*)"/g, 'class="$1 max-w-lg$2"')
			.replace(/class="([^"]*)\s*size-full([^"]*)"/g, 'class="$1 max-w-full$2"')

			// Handle WordPress button blocks
			.replace(
				/<div class="wp-block-button([^"]*)"([^>]*)>/g,
				'<div class="wp-block-button$1 mb-6 mt-6"$2>',
			)
			.replace(
				/<a class="wp-block-button__link([^"]*)"([^>]*)>/g,
				'<a class="wp-block-button__link$1 inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium no-underline"$2>',
			)

			// Handle WordPress code blocks
			.replace(
				/<pre class="wp-block-code([^"]*)"([^>]*)>/g,
				'<pre class="wp-block-code$1 bg-gray-900 text-gray-100 p-6 rounded-lg mb-8 mt-8 overflow-x-auto"$2>',
			)

			// Handle WordPress separator blocks
			.replace(
				/<hr class="wp-block-separator([^"]*)"([^>]*)>/g,
				'<hr class="wp-block-separator$1 my-12 border-gray-300"$2>',
			)

			// Handle WordPress table blocks
			.replace(
				/<figure class="wp-block-table([^"]*)"([^>]*)>/g,
				'<figure class="wp-block-table$1 mb-8 mt-8 overflow-x-auto"$2>',
			)
			.replace(
				/<table([^>]*)>/g,
				'<table$1 class="min-w-full border-collapse border border-gray-300">',
			)
			.replace(
				/<th([^>]*)>/g,
				'<th$1 class="border border-gray-300 px-4 py-2 bg-gray-100 font-semibold text-left">',
			)
			.replace(
				/<td([^>]*)>/g,
				'<td$1 class="border border-gray-300 px-4 py-2">',
			)

			// Handle WordPress video blocks
			.replace(
				/<figure class="wp-block-video([^"]*)"([^>]*)>/g,
				'<figure class="wp-block-video$1 mb-8 mt-8"$2>',
			)
			.replace(
				/<video([^>]*)>/g,
				'<video$1 class="w-full rounded-lg shadow-md">',
			)

			// Handle WordPress embed blocks
			.replace(
				/<figure class="wp-block-embed([^"]*)"([^>]*)>/g,
				'<figure class="wp-block-embed$1 mb-8 mt-8"$2>',
			)

			// Ensure links have proper styling
			.replace(
				/<a(?![^>]*class=["'][^"']*wp-block-button__link)([^>]*)>/g,
				'<a$1 class="text-steerOrange underline font-medium hover:text-orange-600 hover:bg-orange-50 transition-all">',
			)

			// Handle images without WordPress classes (fallback)
			.replace(
				/<img(?![^>]*class=)([^>]*)>/g,
				'<img$1 class="rounded-lg shadow-md max-w-full h-auto mb-6 mt-6">',
			)

			.replace(
				/<img([^>]*)class="([^"]*)"([^>]*)>/g,
				'<img$1class="$2 w-full max-w-full h-auto rounded-lg shadow-md mb-6 mt-6"$3>',
			)
			// Handle images without any class attribute (fallback)
			.replace(
				/<img(?![^>]*class=)([^>]*)>/g,
				'<img$1 class="w-full max-w-full h-auto rounded-lg shadow-md mb-6 mt-6">',
			)
			// Remove any max-w-xs, max-w-lg, etc. from images
			.replace(/max-w-xs|max-w-lg|max-w-sm|max-w-md|max-w-xl/g, "max-w-full");

		return processedHtml;
	};

	const createMarkup = (html) => {
		const processedHtml = processWordPressContent(html);
		return { __html: processedHtml };
	};

	const handleBackClick = () => {
		navigate("/blogs");
	};

	// Enhanced link handling
	useEffect(() => {
		if (post) {
			const handleLinkClick = (e) => {
				const link = e.target.closest("a");
				if (link && !link.classList.contains("wp-block-button__link")) {
					if (link.hostname !== window.location.hostname) {
						e.preventDefault();
						window.open(link.href, "_blank", "noopener,noreferrer");
					}
				}
			};

			document.addEventListener("click", handleLinkClick);
			return () => document.removeEventListener("click", handleLinkClick);
		}
	}, [post]);

	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.2,
				delayChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.6,
				ease: "easeOut",
				stiffness: 100,
			},
		},
	};

	const contentVariants = {
		hidden: { opacity: 0, y: 30 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.8,
				ease: "easeOut",
			},
		},
	};

	if (loading) {
		return (
			<motion.div
				className="animate-pulse"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.3 }}
			>
				<div className="h-6 bg-gray-300 rounded mb-6 w-24"></div>
				<div className="h-12 bg-gray-300 rounded mb-4"></div>
				<div className="h-4 bg-gray-300 rounded mb-6 w-1/2"></div>
				<div className="h-64 bg-gray-300 rounded mb-8"></div>
				<div className="space-y-4">
					<div className="h-4 bg-gray-300 rounded"></div>
					<div className="h-4 bg-gray-300 rounded"></div>
					<div className="h-4 bg-gray-300 rounded w-3/4"></div>
				</div>
			</motion.div>
		);
	}

	if (error) {
		return (
			<motion.div
				className="max-w-4xl mx-auto px-4 py-8"
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.5 }}
			>
				<motion.button
					onClick={handleBackClick}
					className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors"
					whileHover={{ x: -5 }}
					transition={{ type: "spring", stiffness: 400, damping: 30 }}
				>
					<ArrowLeft className="w-4 h-4 mr-2" />
					Back to Blog
				</motion.button>
				<div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
					<h2 className="text-2xl font-semibold text-red-800 mb-4">
						Error Loading Article
					</h2>
					<p className="text-red-600 mb-6">{error}</p>
					<motion.button
						onClick={fetchPost}
						className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors mr-4"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
					>
						Try Again
					</motion.button>
					<Link
						to="/blogs"
						className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
					>
						Back to Blog
					</Link>
				</div>
			</motion.div>
		);
	}

	if (!post) return null;

	return (
		<>
			{post.yoast_head_json && (
				<Helmet>
					<title>{post.yoast_head_json.title || post.title.rendered}</title>
					<meta
						name="description"
						content={
							post.yoast_head_json.description ||
							post.excerpt?.rendered?.replace(/<[^>]+>/g, "").slice(0, 160)
						}
					/>
					<meta
						property="og:title"
						content={post.yoast_head_json.og_title || post.title.rendered}
					/>
					<meta
						property="og:description"
						content={
							post.yoast_head_json.og_description ||
							post.excerpt?.rendered?.replace(/<[^>]+>/g, "").slice(0, 160)
						}
					/>
					<meta
						property="og:url"
						content={post.yoast_head_json.og_url || window.location.href}
					/>
					<meta
						property="og:site_name"
						content={
							post.yoast_head_json.og_site_name || "Steer Smart Driving School"
						}
					/>
					<meta
						property="article:published_time"
						content={post.yoast_head_json.article_published_time || post.date}
					/>
					{post.yoast_head_json.og_image &&
						post.yoast_head_json.og_image[0] && (
							<meta
								property="og:image"
								content={post.yoast_head_json.og_image[0].url}
							/>
						)}
					<meta name="twitter:card" content="summary_large_image" />
					<meta
						name="twitter:title"
						content={post.yoast_head_json.twitter_title || post.title.rendered}
					/>
					<meta
						name="twitter:description"
						content={
							post.yoast_head_json.twitter_description ||
							post.excerpt?.rendered?.replace(/<[^>]+>/g, "").slice(0, 160)
						}
					/>
					{post.yoast_head_json.twitter_image && (
						<meta
							name="twitter:image"
							content={post.yoast_head_json.twitter_image}
						/>
					)}
					<script type="application/ld+json">
						{JSON.stringify(post.yoast_head_json.schema)}
					</script>
				</Helmet>
			)}
			<motion.article
				className="flex flex-col gap-4 py-6 md:py-12 px-0 lg:px-48"
				variants={containerVariants}
				initial="hidden"
				animate="visible"
			>
				{/* Article Header */}
				<motion.header variants={itemVariants}>
					{post.title?.rendered && (
						<motion.h1
							className="text-2xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight font-generalSans"
							dangerouslySetInnerHTML={createMarkup(post.title.rendered)}
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, ease: "easeOut" }}
						/>
					)}
				</motion.header>

				{/* Article Content */}
				<motion.div
					className="wordpress-content max-w-none overflow-hidden flex flex-col gap-6 text-sm lg:text-base"
					style={{
						display: "flow-root",
					}}
					variants={contentVariants}
					dangerouslySetInnerHTML={createMarkup(post.content.rendered)}
				/>

				{/* Tags and Navigation Footer */}
				<motion.footer className="flex flex-col gap-6" variants={itemVariants}>
					{getTags(post).length > 0 && (
						<motion.div
							className="flex items-center flex-wrap gap-2"
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.5, duration: 0.6 }}
						>
							<span className="text-gray-500 mr-2">Tags:</span>
							{getTags(post).map((tag, index) => (
								<motion.div
									key={tag.id}
									initial={{ opacity: 0, scale: 0.8 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{
										delay: 0.6 + index * 0.1,
										duration: 0.4,
										type: "spring",
										stiffness: 300,
									}}
								>
									<Link
										to={`/articles?tag=${encodeURIComponent(tag.name)}`}
										className="px-2 py-1 bg-orange-100 text-orange-800 text-sm rounded-full hover:bg-orange-200 transition-colors cursor-pointer inline-block"
									>
										#{tag.name}
									</Link>
								</motion.div>
							))}
						</motion.div>
					)}

					{/* Back Navigation */}
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ delay: 0.4, duration: 0.6 }}
					>
						<Link
							to="/articles"
							className="inline-flex items-center text-steerOrange font-medium group"
						>
							<motion.div
								className="flex items-center"
								whileHover={{ x: -5 }}
								transition={{ type: "spring", stiffness: 400, damping: 30 }}
							>
								<ArrowLeft className="w-4 h-4 mr-2 group-hover:transform group-hover:-translate-x-1 transition-transform" />
								Back to all posts
							</motion.div>
						</Link>
					</motion.div>
				</motion.footer>
			</motion.article>
		</>
	);
}
